import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";

import { baseUrl } from "config/config";
import { getApiCall, postApiCall } from "../../services/apiHelper";
import { RootState } from "../store";

interface SustainabilityResponse {
  sustainabilityData: {
    [propertyId: string]: PropertySustainabilityData;
  };
}

interface PropertySustainabilityData {
  propertyId: string;
  invId: string;
  portfolioId: string | null;
  portfolioName: string | null;
  riverFloodDefendedRisk: string | null;
  seaLevelRisk: string | null;
  wildfireRisk: string | null;
  heatStress: string | null;
  droughtStress: string | null;
  countOfMitigationMeasures: number;
  complianceStatusAccToSsbb: string | null;
  decarbonizationAssessmentStatus: string | null;
  buildingPerformanceStandard: string;
  mitigationPlan: string;
  physicalRiskComment: string;
  exposureToAdditionalRegulatory: string;
  complianceStatusComment: string;
  includeCostToProcedure: string;
  includeProjectsComesIntoCompliance: string;
  regulatoryRiskComment: string;
  decarbonizationAssessmentStatusComment: string;
  decarbonizationAssessmentComment: string;
  ssbbFeatures: string;
  ssbbStandards: string;
  ssbbStandardsComment: string;
  standardEnergyAuditCost: string;
  includeCostForStandardEnergyAudit: string;
}


interface InitialState {
  data: SustainabilityResponse,
  error: string,
  loading: boolean,
  cacheKey: string | undefined,
  isNoData: boolean
};

const initialState: InitialState = {
  data: {} as SustainabilityResponse,
  error: '',
  loading: false,
  cacheKey: undefined,
  isNoData: false
};

const sustainabilityDataSlice = createSlice({
  name: "sustainabilityData",
  initialState,
  reducers: {
    resetState: (state) => {
      state.data = {} as SustainabilityResponse;
      state.error = "";
      state.loading = false;
      state.cacheKey = undefined;
      state.isNoData = false;
    },
  },
  extraReducers: (builder) => {
    // eslint-disable-next-line   
    builder.addCase(getSustainabilityData.pending, (state) => {
      state.loading = true;
    }),
      builder.addCase(getSustainabilityData.fulfilled, (state, action: PayloadAction<SustainabilityResponse>) => {
        state.loading = false;
        state.data = action.payload;
        state.cacheKey = 'sustainabilityCache';
      }),
      builder.addCase(getSustainabilityData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'something went wrong';
      })
  }
});

//export const getsustainabilityData = createAsyncThunk("getsustainabilityData", async (payload: any) => {
export const getSustainabilityData = createAsyncThunk
  <
    SustainabilityResponse,
    any,
    { state: RootState }
  >
  ("getsustainabilityData", async (payload: any, { getState, rejectWithValue }) => {
    const state = getState().sustainabilityData;
    const sustainabilityData = getState().updateData?.sustainabilityData

    if (state.cacheKey === 'sustainabilityCache' ) {
      const currentData = state.data.sustainabilityData || {};
      const mergedData: any = {};
      
      Object.keys(currentData).forEach(propertyId => {
        const stateProperty = currentData[propertyId];
        const updateProperty = sustainabilityData?.[propertyId] || {};
        
        mergedData[propertyId] = {
          ...stateProperty,
          decarbonizationAssessmentComment: updateProperty.decarbonizationAssessmentComment || stateProperty.decarbonizationAssessmentComment,
          decarbonizationAssessmentStatusComment: updateProperty.decarbonizationAssessmentStatusComment || stateProperty.decarbonizationAssessmentStatusComment,
          exposureToAdditionalRegulatory: updateProperty.exposureToAdditionalRegulatory || stateProperty.exposureToAdditionalRegulatory,
          complianceStatusComment: updateProperty.complianceStatusComment || stateProperty.complianceStatusComment,
          includeCostToProcedure: updateProperty.includeCostToProcedure || stateProperty.includeCostToProcedure,
          includeProjectsComesIntoCompliance: updateProperty.includeProjectsComesIntoCompliance || stateProperty.includeProjectsComesIntoCompliance,
          mitigationPlan: updateProperty.mitigationPlan || stateProperty.mitigationPlan,
          physicalRiskComment: updateProperty.physicalRiskComment || stateProperty.physicalRiskComment,
          regulatoryRiskComment: updateProperty.regulatoryRiskComment || stateProperty.regulatoryRiskComment,
          ssbbFeatures: updateProperty.ssbbFeatures || stateProperty.ssbbFeatures,
          ssbbStandards: updateProperty.ssbbStandards || stateProperty.ssbbStandards,
          ssbbStandardsComment: updateProperty.ssbbStandardsComment || stateProperty.ssbbStandardsComment,
          includeCostForStandardEnergyAudit: updateProperty.includeCostForStandardEnergyAudit || stateProperty.includeCostForStandardEnergyAudit
        };
      });
      
      return { sustainabilityData: mergedData };
    }

    const response = await postApiCall(`${baseUrl}Sustainability/GetSustainabilityData`,payload).then((res: any) =>
      res);
    console.log("response",response)
    return { sustainabilityData: response.sustainabilityData || null };
  });

export const { resetState: resetSustainabilityDataStore } = sustainabilityDataSlice.actions;

export default sustainabilityDataSlice.reducer;
